/**
 * FlightTimeline Component
 * 
 * Displays duty periods with flights and layovers in a vertical timeline
 */

import React from 'react';

const FlightTimeline = ({ dutyPeriods, meta }) => {
  // Format time with timezone indicator (L=Local, Z=Zulu/UTC, B=Base)
  const formatTimeWithZone = (timeObj, showZone = 'local') => {
    if (!timeObj) return 'N/A';
    
    // Extract time strings from nested object
    const utcString = typeof timeObj === 'string' ? timeObj : timeObj.utc;
    const localString = timeObj.local_wall;
    
    if (!utcString) return 'N/A';
    
    // Format based on requested zone
    let timeStr = '';
    let suffix = '';
    
    if (showZone === 'utc' || showZone === 'zulu') {
      const date = new Date(utcString);
      timeStr = date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      });
      suffix = 'Z';
    } else if (showZone === 'local') {
      const date = new Date(localString || utcString);
      timeStr = date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      suffix = 'L';
    } else if (showZone === 'base') {
      // Base time - use meta.base timezone if available, otherwise local
      const date = new Date(localString || utcString);
      timeStr = date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      suffix = 'B';
    }
    
    return `${timeStr}${suffix}`;
  };
  
  // Format time as HH:MM (local) - for backwards compatibility
  const formatTime = (timeObj) => {
    if (!timeObj) return 'N/A';
    // Handle nested time object with UTC field
    const isoString = typeof timeObj === 'string' ? timeObj : timeObj.utc || timeObj.local_wall;
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };
  
  // Format date as "Mon, Jan 5"
  const formatDate = (timeObj) => {
    if (!timeObj) return 'N/A';
    // Handle nested time object with UTC field
    const isoString = typeof timeObj === 'string' ? timeObj : timeObj.utc || timeObj.local_wall;
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  // Calculate duration between two times
  const calculateDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    // Handle nested time objects
    const startStr = typeof start === 'string' ? start : start.utc || start.local_wall;
    const endStr = typeof end === 'string' ? end : end.utc || end.local_wall;
    if (!startStr || !endStr) return 'N/A';
    const startTime = new Date(startStr);
    const endTime = new Date(endStr);
    const diffMinutes = Math.round((endTime - startTime) / 60000);
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };
  
  // Calculate connection time in minutes between two flights
  const calculateConnectionMinutes = (arrivalTime, departureTime) => {
    if (!arrivalTime || !departureTime) return null;
    const arrStr = typeof arrivalTime === 'string' ? arrivalTime : arrivalTime.utc || arrivalTime.local_wall;
    const depStr = typeof departureTime === 'string' ? departureTime : departureTime.utc || departureTime.local_wall;
    if (!arrStr || !depStr) return null;
    const arrival = new Date(arrStr);
    const departure = new Date(depStr);
    return Math.round((departure - arrival) / 60000); // Minutes
  };
  
  return (
    <div className="space-y-6">
      {dutyPeriods && dutyPeriods.map((duty, dutyIdx) => (
        <div key={dutyIdx} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          {/* Duty Period Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">
                  Day {duty.duty_sequence} - {formatDate(duty.report)}
                </h3>
                <div className="text-sm opacity-90 mt-1 font-mono">
                  <span className="mr-1">Report:</span>
                  <span className="mr-2">{formatTimeWithZone(duty.report, 'local')}</span>
                  <span className="mr-2">{formatTimeWithZone(duty.report, 'zulu')}</span>
                  <span className="mr-3">{formatTimeWithZone(duty.report, 'base')}</span>
                  <span className="mr-1">→ Release:</span>
                  <span className="mr-2">{formatTimeWithZone(duty.release, 'local')}</span>
                  <span className="mr-2">{formatTimeWithZone(duty.release, 'zulu')}</span>
                  <span>{formatTimeWithZone(duty.release, 'base')}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">
                  {calculateDuration(duty.report, duty.release)}
                </p>
                <p className="text-xs opacity-90">Duty Time</p>
              </div>
            </div>
          </div>
          
          {/* Equipment Badge */}
          {meta?.equipment && (
            <div className="px-4 pt-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                ✈️ Equipment: {meta.equipment}
              </span>
            </div>
          )}
          
          {/* Flight Legs */}
          <div className="p-4 space-y-3">
            {duty.legs && duty.legs.map((leg, legIdx) => {
              const isDeadhead = leg.is_deadhead || leg.flight_type === 'DEADHEAD';
              
              // Determine flight type indicators
              const isInternational = leg.is_international || false;
              const isRedEye = leg.is_red_eye || (leg.overnight_crossing && !isDeadhead);
              const isEarlyAM = !isDeadhead && leg.sched_out && (() => {
                const depTime = new Date(typeof leg.sched_out === 'string' ? leg.sched_out : leg.sched_out.utc);
                const hour = depTime.getUTCHours();
                return hour >= 4 && hour < 8; // 04:00-07:59 UTC
              })();
              const isLateNight = !isDeadhead && leg.sched_in && (() => {
                const arrTime = new Date(typeof leg.sched_in === 'string' ? leg.sched_in : leg.sched_in.utc);
                const hour = arrTime.getUTCHours();
                return hour >= 22 || hour < 4; // 22:00-03:59 UTC
              })();
              
              return (
                <div 
                  key={legIdx}
                  className={`
                    border-2 rounded-lg p-4
                    ${isDeadhead 
                      ? 'border-amber-300 bg-amber-50' 
                      : 'border-gray-200 bg-white'
                    }
                  `}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isDeadhead && (
                        <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded">
                          DH
                        </span>
                      )}
                      {isInternational && (
                        <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded">
                          🌍 INTL
                        </span>
                      )}
                      {isRedEye && (
                        <span className="px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded">
                          🌙 Red-Eye
                        </span>
                      )}
                      {isEarlyAM && (
                        <span className="px-2 py-1 bg-orange-500 text-white text-xs font-bold rounded">
                          🌅 Early AM
                        </span>
                      )}
                      {isLateNight && (
                        <span className="px-2 py-1 bg-indigo-500 text-white text-xs font-bold rounded">
                          🌃 Late Night
                        </span>
                      )}
                      <span className="font-mono font-bold text-lg text-gray-900">
                        {leg.flight_number || 'N/A'}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-600">
                        Leg {legIdx + 1} of {duty.legs.length}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">
                        {leg.sched_block?.minutes ? `${Math.floor(leg.sched_block.minutes / 60)}:${(leg.sched_block.minutes % 60).toString().padStart(2, '0')}` : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500">Block Time</p>
                    </div>
                  </div>
                  
                  {/* Route */}
                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase">From</p>
                      <p className="text-2xl font-bold text-gray-900">{leg.origin || 'N/A'}</p>
                      <div className="text-sm text-gray-600 font-mono">
                        <span className="mr-2">{formatTimeWithZone(leg.sched_out, 'local')}</span>
                        <span className="mr-2">{formatTimeWithZone(leg.sched_out, 'zulu')}</span>
                        <span>{formatTimeWithZone(leg.sched_out, 'base')}</span>
                      </div>
                    </div>
                    
                    <div className="text-2xl text-gray-300">→</div>
                    
                    <div className="flex-1 text-right">
                      <p className="text-xs text-gray-500 uppercase">To</p>
                      <p className="text-2xl font-bold text-gray-900">{leg.destination || 'N/A'}</p>
                      <div className="text-sm text-gray-600 font-mono">
                        <span className="mr-2">{formatTimeWithZone(leg.sched_in, 'local')}</span>
                        <span className="mr-2">{formatTimeWithZone(leg.sched_in, 'zulu')}</span>
                        <span>{formatTimeWithZone(leg.sched_in, 'base')}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Aircraft Type */}
                  {leg.equipment && (
                    <div className="text-xs text-gray-500">
                      Aircraft: <span className="font-mono text-gray-700">{leg.equipment}</span>
                    </div>
                  )}
                  
                  {/* Connection Time (if not last leg in duty period) */}
                  {legIdx < duty.legs.length - 1 && (() => {
                    const nextLeg = duty.legs[legIdx + 1];
                    const connectionMinutes = calculateConnectionMinutes(leg.sched_in, nextLeg.sched_out);
                    if (connectionMinutes !== null) {
                      const hours = Math.floor(connectionMinutes / 60);
                      const minutes = connectionMinutes % 60;
                      const isShortConnection = connectionMinutes < 60;
                      
                      return (
                        <div className={`
                          mt-3 pt-3 border-t border-dashed
                          ${isShortConnection ? 'border-red-300' : 'border-gray-300'}
                        `}>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">
                              ⏱️ Connection Time to Next Flight
                            </span>
                            <span className={`
                              font-mono font-semibold
                              ${isShortConnection ? 'text-red-600' : 'text-gray-700'}
                            `}>
                              {hours}:{minutes.toString().padStart(2, '0')}
                              {isShortConnection && ' ⚠️'}
                            </span>
                          </div>
                          {isShortConnection && (
                            <p className="text-xs text-red-600 mt-1">
                              Short connection - less than 1 hour
                            </p>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              );
            })}
            
            {/* Removed old connection time message - now shown per leg */}
          </div>
          
          {/* Layover Card (if overnight) */}
          {duty.layover_hotel_name && (
            <div className="mx-4 mb-4 border-t-2 border-dashed border-gray-200 pt-4">
              <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-300 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0 mt-1">🏨</span>
                  <div className="flex-1">
                    <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                      🏨 Layover Hotel
                      <span className="text-xs font-normal text-green-700 bg-green-200 px-2 py-1 rounded">
                        {duty.layover_duration?.minutes 
                          ? `${Math.floor(duty.layover_duration.minutes / 60)}:${(duty.layover_duration.minutes % 60).toString().padStart(2, '0')}`
                          : duty.layover_duration || 'N/A'
                        }
                      </span>
                    </h4>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">📍</span>
                        <span className="font-semibold text-green-900">
                          {duty.layover_hotel_name}
                        </span>
                        <span className="text-green-700">
                          ({duty.layover_location})
                        </span>
                      </div>
                      
                      {duty.layover_phone && (
                        <div className="flex items-center gap-2">
                          <span className="text-green-600">📞</span>
                          <a 
                            href={`tel:${duty.layover_phone}`}
                            className="text-green-700 hover:text-green-900 hover:underline"
                          >
                            {duty.layover_phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
      
      {/* TAFB Summary (if multiple duty periods) */}
      {dutyPeriods && dutyPeriods.length > 1 && (
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-purple-300 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl text-purple-700">⏱️</span>
              <div>
                <h4 className="font-bold text-purple-900">Time Away From Base (TAFB)</h4>
                <p className="text-sm text-purple-700">
                  From first report to final release
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-purple-900">
                {calculateDuration(
                  dutyPeriods[0]?.report,
                  dutyPeriods[dutyPeriods.length - 1]?.release
                )}
              </p>
              <p className="text-xs text-purple-700">Total TAFB</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightTimeline;
